import { useReducer, useCallback, useRef, useEffect } from "react"
import dayjs from "dayjs"

import { formatStringToFloatDecimalCase } from "src/utils/format-decimal"

const DATE_FORMAT = "YYYY-MM-DD"

const initialState = {
    loading: false,
    ultimo: { data_referencia: "", spread_credito: "", percentual_pupar: "" },
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
    openModal: false,
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_LOADING_TABLE":
            return { ...state, loading: action.payload }
        case "SET_ULTIMO":
            return { ...state, ultimo: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        case "SET_MODAL":
            return { ...state, openModal: action.payload }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") =>
    [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })

export const usePrecosProcessados = (
    emissaoService,
    cetipIsin,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const loadedRef = useRef(cetipIsin)
    
    useEffect(() => {
        if (loadedRef.current === cetipIsin) return

        loadedRef.current = cetipIsin
    
        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: [] })
        dispatch({ type: "CLOSE_ALERT" })
            
    }, [cetipIsin])

    const validateValue = useCallback(() => cetipIsin?.length > 4, [cetipIsin])

    const buildTable = useCallback((dadosCabecalho) => {
        const { cetip, isin } = dadosCabecalho || {}
        
        dispatch({
            type: "SET_TABLE",
            payload: [
                {
                    cells: [
                        `CETIP: ${cetip || "N/A"}`,
                        `ISIN: ${isin || "N/A"}`,
                    ],
                },
            ],
        })
    }, [])

    const loadTableRows = useCallback(async () => {
        if (!validateValue()) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CETIP não informado ou inválido.",
                    type: "warning",
                },
            })
            // Monta cabeçalho vazio por precaução
            buildTable({
                cetip: dadosEmpresa?.CETIP || cetipIsin,
                isin: dadosEmpresa?.ISIN
            })
            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_MODAL", payload: true })
            return
        }

        dispatch({ type: "SET_LOADING", payload: true })
        
        try {
            const response = await emissaoService.precosProcessados(
                "get",
                `?CETIP=${cetipIsin.toUpperCase()}&DataReferencia__desc=true`
            )
            
            // Extração segura do array de resultados
            const listaResultados = response?.body?.results || (Array.isArray(response?.body) ? response.body : [])

            if (!listaResultados || listaResultados.length === 0) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
                dispatch({ type: "SET_ROWS", payload: [] })

                // Monta tabela com fallback usando os dados locais
                buildTable({
                    cetip: cetipIsin || dadosEmpresa?.CETIP,
                    isin: dadosEmpresa?.ISIN
                })
            } else {
                dispatch({ type: "CLOSE_ALERT" })
                
                const rows = ordenaPorDataReferencia(listaResultados, "desc").map(
                    (obj, i) => ({ ...obj, id: i })
                )
                
                const primeiraLinha = rows[0] || {}

                // Monta a tabela priorizando os dados da API
                buildTable({
                    cetip: primeiraLinha.CETIP || primeiraLinha.cetip || cetipIsin || dadosEmpresa?.CETIP,
                    isin: primeiraLinha.ISIN || primeiraLinha.Isin || primeiraLinha.isin || dadosEmpresa?.ISIN
                })

                dispatch({ type: "SET_ROWS", payload: rows })
            }
            dispatch({ type: "SET_MODAL", payload: true })
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao buscar dados ou CETIP não encontrado.",
                    type: "warning",
                },
            })
            dispatch({ type: "SET_ROWS", payload: [] })
            
            // Mantém os cabeçalhos visíveis mesmo em caso de erro
            buildTable({
                cetip: cetipIsin || dadosEmpresa?.CETIP,
                isin: dadosEmpresa?.ISIN
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [cetipIsin, emissaoService, validateValue, buildTable, dadosEmpresa])

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]
        if (!state.rows.length) {
            lastItem = {
                CETIP: cetipIsin,
                DataReferencia: dayjs().format(DATE_FORMAT),
                TaxaMTM: 0,
                PercentualPUPAR: 0,
                SpreadCredito: 0,
            }
        }
        dispatch({ type: "SET_LOADING", payload: true })
        try {
            await emissaoService.precosProcessados("post", lastItem)
            await loadTableRows()
        } catch {
             dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar novo registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipIsin, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            const { ID } = row
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.precosProcessados("delete", ID)
                await loadTableRows()
            } catch {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            const { ID } = row
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.precosProcessados("patch", ID, row)
                const updated = state.rows.map((r) =>
                    r.ID === ID ? { ...r, ...row } : r
                )
                dispatch({ type: "SET_ROWS", payload: updated })
                return row
            } catch {
                 dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, state.rows]
    )

    const formatValue = useCallback((params, min, max) => {
        const { value } = params
        return formatStringToFloatDecimalCase(value, min, max)
    }, [])

    return {
        state,
        actions: {
            loadTableRows,
            addRow,
            deleteRow,
            updateRow,
            setAlert: (payload) => dispatch({ type: "SET_ALERT", payload }),
            closeAlert: () => dispatch({ type: "CLOSE_ALERT" }),
            setModal: (open) => dispatch({ type: "SET_MODAL", payload: open }),
            formatValue,
        },
    }
}